import React, { useState } from "react";
import {
  Home,
  Users,
  Building2,
  Upload,
  Calendar,
  DollarSign,
  Shield,
  BarChart3,
  Briefcase,
  Calculator,
  FileSpreadsheet,
  UserPlus,
  UserCheck,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Info,
  AlertTriangle,
  CheckCircle,
  Key,
} from "lucide-react";
import "../styles/Docs.css";

const sections = [
  {
    id: "overview",
    icon: BookOpen,
    title: "סקירה כללית של המערכת",
    color: "#3b82f6",
  },
  {
    id: "permissions",
    icon: Key,
    title: "הרשאות משתמשים",
    color: "#8b5cf6",
  },
  {
    id: "navbar",
    icon: Home,
    title: "סרגל הניווט",
    color: "#06b6d4",
  },
  {
    id: "dashboard",
    icon: BarChart3,
    title: "לוח בקרה (Dashboard)",
    color: "#10b981",
  },
  {
    id: "employees",
    icon: Users,
    title: "ניהול עובדים",
    color: "#f59e0b",
  },
  {
    id: "customers",
    icon: Building2,
    title: "ניהול לקוחות",
    color: "#ef4444",
  },
  {
    id: "upload",
    icon: Upload,
    title: "העלאת קבצים",
    color: "#6366f1",
  },
  {
    id: "presence",
    icon: Calendar,
    title: "נוכחות",
    color: "#0891b2",
  },
  {
    id: "enforcement",
    icon: Shield,
    title: "תגבור",
    color: "#dc2626",
  },
  {
    id: "salaries",
    icon: DollarSign,
    title: "משכורות",
    color: "#16a34a",
  },
  {
    id: "users",
    icon: UserPlus,
    title: "ניהול משתמשים",
    color: "#7c3aed",
  },
  {
    id: "approval",
    icon: UserCheck,
    title: "אישור עובדים חדשים",
    color: "#059669",
  },
  {
    id: "profit",
    icon: Calculator,
    title: "חישוב רווח",
    color: "#d97706",
  },
  {
    id: "report",
    icon: FileSpreadsheet,
    title: "דוח אקסל",
    color: "#2563eb",
  },
  {
    id: "flows",
    icon: CheckCircle,
    title: "תהליכים מרכזיים - מדריך שלב אחר שלב",
    color: "#059669",
  },
];

const Docs = () => {
  const [openSections, setOpenSections] = useState({ overview: true });

  const toggle = (id) =>
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));

  const isOpen = (id) => !!openSections[id];

  return (
    <div className="docs-page" dir="rtl">
      <div className="docs-hero">
        <BookOpen size={40} />
        <div>
          <h1>מדריך שימוש במערכת ניהול עובדים קורל</h1>
          <p>מדריך מלא בעברית לכל הפונקציות, הדפים והתהליכים במערכת</p>
        </div>
      </div>

      <div className="docs-layout">
        {/* Sidebar TOC */}
        <nav className="docs-toc">
          <p className="toc-title">תוכן עניינים</p>
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="toc-link"
              style={{ "--accent": s.color }}
            >
              <s.icon size={15} />
              <span>{s.title}</span>
            </a>
          ))}
        </nav>

        {/* Main content */}
        <main className="docs-main">

          {/* ── סקירה כללית ── */}
          <section id="overview" className="docs-section">
            <button className="section-header" onClick={() => toggle("overview")}>
              <div className="section-title" style={{ color: "#3b82f6" }}>
                <BookOpen size={22} />
                <span>סקירה כללית של המערכת</span>
              </div>
              {isOpen("overview") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("overview") && (
              <div className="section-body">
                <p>
                  מערכת ניהול עובדים קורל היא פלטפורמה לניהול כוח אדם, נוכחות ומשכורות.
                  המערכת תומכת בשני סוגי עובדים עיקריים:
                </p>
                <div className="info-cards">
                  <div className="info-card blue">
                    <strong>קבלן</strong>
                    <p>עובדים עצמאיים שמקבלים תשלום לפי שעה. השכר מחושב אוטומטית מתוך שעות ה-T&M.</p>
                  </div>
                  <div className="info-card green">
                    <strong>חברה</strong>
                    <p>עובדי חברה עם משכורת חודשית קבועה, מנוהלים דרך קובץ חברות.</p>
                  </div>
                  <div className="info-card purple">
                    <strong>קבלן ו חברה</strong>
                    <p>עובדים ששייכים לשני הסוגים — חישוב מעורב.</p>
                  </div>
                </div>
                <div className="tip-box">
                  <Info size={16} />
                  <span>המערכת מחברת בין שני סוגי קבצי Excel: <strong>T&M</strong> (זמן וחומרים) ו-<strong>חברות</strong> (גיליון לכל לקוח).</span>
                </div>
              </div>
            )}
          </section>

          {/* ── הרשאות ── */}
          <section id="permissions" className="docs-section">
            <button className="section-header" onClick={() => toggle("permissions")}>
              <div className="section-title" style={{ color: "#8b5cf6" }}>
                <Key size={22} />
                <span>הרשאות משתמשים</span>
              </div>
              {isOpen("permissions") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("permissions") && (
              <div className="section-body">
                <p>כל משתמש במערכת מקבל הרשאה שקובעת אילו עמודים ופעולות הוא יכול לגשת אליהם:</p>
                <table className="docs-table">
                  <thead>
                    <tr>
                      <th>הרשאה</th>
                      <th>תיאור</th>
                      <th>גישה</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><span className="badge owner">בעלים</span></td>
                      <td>גישה מלאה לכל המערכת</td>
                      <td>הכל, כולל שותפויות ודשבורד מתקדם</td>
                    </tr>
                    <tr>
                      <td><span className="badge admin">מנהל</span></td>
                      <td>ניהול יום-יומי מלא</td>
                      <td>עובדים, לקוחות, העלאה, נוכחות, משכורות, דוחות, ניהול משתמשים</td>
                    </tr>
                    <tr>
                      <td><span className="badge site-admin">מנהל אתר</span></td>
                      <td>גישה למשכורות</td>
                      <td>משכורות קבלנים + חברה + שניהם</td>
                    </tr>
                    <tr>
                      <td><span className="badge viewer">צופה</span></td>
                      <td>גישת קריאה בלבד</td>
                      <td>עובדים, לקוחות, נוכחות, תגבור</td>
                    </tr>
                    <tr>
                      <td><span className="badge salary-viewer">משכורות</span></td>
                      <td>צפייה במשכורות בלבד</td>
                      <td>עמוד משכורות בלבד</td>
                    </tr>
                    <tr>
                      <td><span className="badge recruiter">מגייס</span></td>
                      <td>הוספת עובדים חדשים</td>
                      <td>עמוד הוספת עובד בלבד</td>
                    </tr>
                    <tr>
                      <td><span className="badge payer">משלם</span></td>
                      <td>ביצוע תשלום משכורות</td>
                      <td>עמוד תשלום משכורות בלבד</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── סרגל הניווט ── */}
          <section id="navbar" className="docs-section">
            <button className="section-header" onClick={() => toggle("navbar")}>
              <div className="section-title" style={{ color: "#06b6d4" }}>
                <Home size={22} />
                <span>סרגל הניווט</span>
              </div>
              {isOpen("navbar") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("navbar") && (
              <div className="section-body">
                <p>סרגל הניווט נמצא בחלק העליון של המסך ומכיל את כל הקישורים הראשיים. הוא מורכב מהחלקים הבאים:</p>
                <div className="step-list">
                  <div className="step">
                    <div className="step-num">🏢</div>
                    <div>
                      <strong>לוגו / החלפת חברה</strong>
                      <p>לחיצה על הלוגו בצד שמאל פותחת תפריט לבחירת שותפות פעילה (קורל / גולד קלין). הבחירה משפיעה על הנתונים שמוצגים בכל הדפים.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">📊</div>
                    <div>
                      <strong>דשבורדים</strong>
                      <p>
                        <strong>לוח בקרה ראשי</strong> — סטטיסטיקות כלליות ונוכחות חודש שעבר.<br />
                        <strong>דשבורד מתקדם</strong> — גרפים מפורטים (לבעלים בלבד).
                      </p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">👥</div>
                    <div>
                      <strong>ניהול</strong>
                      <p>מכיל: עובדים, לקוחות, שותפויות, חישוב רווח, ניהול משתמשים, אישור עובדים חדשים.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">📅</div>
                    <div>
                      <strong>נוכחות ותגבור</strong>
                      <p>גישה לדפי הנוכחות והתגבור. בבחירת <em>נוכחות</em> תתבקש לבחור חודש לפני הניווט.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">💰</div>
                    <div>
                      <strong>משכורות</strong>
                      <p>שלושה תת-עמודים: קבלנים, חברה, קבלן וחברה. גם כאן תתבקש לבחור חודש בכניסה.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">📤</div>
                    <div>
                      <strong>העלאת קבצים</strong>
                      <p>גישה מהירה לעמוד העלאת ה-Excel (מנהלים בלבד).</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">📋</div>
                    <div>
                      <strong>דוח אקסל</strong>
                      <p>יצירת דוחות Excel מסודרים לפי חודש (מנהלים בלבד).</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">👤</div>
                    <div>
                      <strong>פרטי משתמש / שינוי סיסמה</strong>
                      <p>לחיצה על שם המשתמש בפינה הימנית פותחת חלון לשינוי סיסמה. כפתור <em>יציאה</em> מנתק מהמערכת.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* ── Dashboard ── */}
          <section id="dashboard" className="docs-section">
            <button className="section-header" onClick={() => toggle("dashboard")}>
              <div className="section-title" style={{ color: "#10b981" }}>
                <BarChart3 size={22} />
                <span>לוח בקרה (Dashboard)</span>
              </div>
              {isOpen("dashboard") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("dashboard") && (
              <div className="section-body">
                <p>עמוד הבית של המערכת. מציג מידע כללי בלחיצה אחת:</p>
                <div className="step-list">
                  <div className="step">
                    <div className="step-num">1</div>
                    <div>
                      <strong>כרטיסי סטטיסטיקה עליונים</strong>
                      <p>סה"כ עובדים, עובדים פעילים, סה"כ לקוחות. לחיצה על כרטיס מנווטת לעמוד הרלוונטי.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">2</div>
                    <div>
                      <strong>נוכחות — חודש שעבר</strong>
                      <p>סיכום שעות: סה"כ, שעות קבלנים, שעות חברה. מחולק לשעות רגילות ושעות סוף שבוע.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">3</div>
                    <div>
                      <strong>לקוחות מובילים</strong>
                      <p>ארבעת הלקוחות עם מספר השעות הגבוה ביותר בחודש שעבר.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">4</div>
                    <div>
                      <strong>פעולות מהירות</strong>
                      <p>קישורים ישירים: העלאת קבצים, ניהול עובדים, יצירת משכורות.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* ── Employees ── */}
          <section id="employees" className="docs-section">
            <button className="section-header" onClick={() => toggle("employees")}>
              <div className="section-title" style={{ color: "#f59e0b" }}>
                <Users size={22} />
                <span>ניהול עובדים</span>
              </div>
              {isOpen("employees") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("employees") && (
              <div className="section-body">
                <p>עמוד זה מציג את כל העובדים הרשומים במערכת ומאפשר ניהול מלא שלהם.</p>

                <h4>מה מוצג בטבלה?</h4>
                <ul className="docs-list">
                  <li>שם עובד, מספר עובד, סוג (קבלן / חברה / קבלן ו חברה)</li>
                  <li>לקוח משויך, שיטת תשלום, תעריף שעתי</li>
                  <li>סטטוס פעיל / לא פעיל</li>
                  <li>תג <strong>גלובלי</strong> — עבור עובדים עם משכורת גלובלית קבועה</li>
                </ul>

                <h4>פעולות זמינות</h4>
                <div className="step-list">
                  <div className="step">
                    <div className="step-num">➕</div>
                    <div>
                      <strong>הוספת עובד חדש</strong>
                      <p>לחץ על כפתור <em>"הוסף עובד"</em>. מלא את הפרטים בחלון שנפתח: שם, מספר עובד, סוג, תעריף שעתי/סופ"ש, שיטת תשלום, פרטי בנק ולקוח משויך.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">✏️</div>
                    <div>
                      <strong>עריכת עובד</strong>
                      <p>לחץ על שורת העובד או על כפתור העריכה. ניתן לשנות את כל פרטי העובד כולל תעריפים, בנק, לקוח וסטטוס פעיל/לא פעיל.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">🗑️</div>
                    <div>
                      <strong>מחיקת עובד</strong>
                      <p>לחץ על אייקון המחיקה בשורת העובד. תתבקש לאשר את הפעולה. <span className="warn-text">שים לב: פעולה זו מוחקת את כל הנתונים הקשורים לעובד!</span></p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">🔍</div>
                    <div>
                      <strong>חיפוש וסינון</strong>
                      <p>ניתן לחפש לפי שם עובד, לסנן לפי סוג עובד, לקוח ושותפות.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">📋</div>
                    <div>
                      <strong>היסטוריית עובד</strong>
                      <p>לחיצה על אייקון ההיסטוריה מציגה את כל שעות הנוכחות והמשכורות של העובד לאורך זמן.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">💰</div>
                    <div>
                      <strong>משכורת גלובלית</strong>
                      <p>בחלון עריכת עובד, ניתן לסמן <em>"משכורת גלובלית (קבועה)"</em> ולהזין סכום קבוע — במקרה זה המשכורת לא מחושבת לפי שעות.</p>
                    </div>
                  </div>
                </div>

                <div className="tip-box">
                  <Info size={16} />
                  <span>עובדים חדשים שנוספו מהעלאת T&M עשויים לחכות לאישור — ראה עמוד <strong>אישור עובדים חדשים</strong>.</span>
                </div>
              </div>
            )}
          </section>

          {/* ── Customers ── */}
          <section id="customers" className="docs-section">
            <button className="section-header" onClick={() => toggle("customers")}>
              <div className="section-title" style={{ color: "#ef4444" }}>
                <Building2 size={22} />
                <span>ניהול לקוחות</span>
              </div>
              {isOpen("customers") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("customers") && (
              <div className="section-body">
                <p>עמוד הלקוחות מציג את כל האתרים/הלקוחות הרשומים במערכת.</p>
                <div className="step-list">
                  <div className="step">
                    <div className="step-num">1</div>
                    <div>
                      <strong>רשימת לקוחות</strong>
                      <p>שם לקוח, שם אתר, קוד אתר, משרד, שותפות. ניתן לחפש וסנן.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">2</div>
                    <div>
                      <strong>הוספת לקוח</strong>
                      <p>לחץ <em>"הוסף לקוח"</em> ומלא שם לקוח, שם אתר, קוד אתר ומשרד.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">3</div>
                    <div>
                      <strong>עריכה ומחיקה</strong>
                      <p>ניתן לערוך את פרטי הלקוח או למחוק אותו. <span className="warn-text">מחיקת לקוח משפיעה על העובדים המשויכים אליו.</span></p>
                    </div>
                  </div>
                </div>
                <div className="tip-box">
                  <Info size={16} />
                  <span>לקוחות נוצרים אוטומטית בעת העלאת קבצי T&M לפי שם הפרויקט.</span>
                </div>
              </div>
            )}
          </section>

          {/* ── Upload ── */}
          <section id="upload" className="docs-section">
            <button className="section-header" onClick={() => toggle("upload")}>
              <div className="section-title" style={{ color: "#6366f1" }}>
                <Upload size={22} />
                <span>העלאת קבצים</span>
              </div>
              {isOpen("upload") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("upload") && (
              <div className="section-body">
                <p>לב המערכת — כאן מעלים את קבצי ה-Excel שמזינים את כל הנתונים.</p>

                <h4>סוגי קבצים</h4>
                <div className="info-cards">
                  <div className="info-card blue">
                    <strong>T&M (זמן וחומרים)</strong>
                    <p>קובץ Excel בפורמט T&M שמכיל שורה לכל יום עבודה של כל עובד. המערכת מחלצת: לקוחות, עובדים, שעות, נוכחות ומשכורות קבלנים.</p>
                  </div>
                  <div className="info-card green">
                    <strong>חברות</strong>
                    <p>קובץ Excel שבו כל גיליון (שיט) הוא לקוח אחד. המערכת מחלצת: עובדי חברה, שעות ונוכחות.</p>
                  </div>
                </div>

                <h4>שלבי העלאה</h4>
                <div className="step-list">
                  <div className="step">
                    <div className="step-num">1</div>
                    <div>
                      <strong>בחר קובץ</strong>
                      <p>לחץ על שדה הקובץ ובחר קובץ <code>.xlsx</code>, <code>.xls</code> או <code>.csv</code>. גודל מקסימלי: 50MB.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">2</div>
                    <div>
                      <strong>בחר סוג נתונים</strong>
                      <p>סמן <em>T&M</em> אם זהו קובץ זמן וחומרים, או <em>חברות</em> אם זהו קובץ חברות.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">3</div>
                    <div>
                      <strong>לחץ "העלה קובץ"</strong>
                      <p>סרגל ההתקדמות יוצג בזמן ההעלאה. עבור T&M — יפתח חלון סיכום עם מספר השורות, העובדים החדשים והמשכורות שחושבו.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">4</div>
                    <div>
                      <strong>עובדים חדשים</strong>
                      <p>עובדים שזוהו בפעם הראשונה ימתינו לאישור בעמוד <em>אישור עובדים חדשים</em>.</p>
                    </div>
                  </div>
                </div>

                <h4>מחיקת נתונים שהועלו</h4>
                <div className="warning-box">
                  <AlertTriangle size={16} />
                  <div>
                    <p>בתחתית עמוד ההעלאה יש אזור <strong>מחיקת נתונים</strong>. בחר חודש וסוג נתונים (חברות / T&M / הכל) ולחץ <em>"מחק נתונים"</em>.</p>
                    <p><strong>שים לב: פעולה זו לא ניתנת לביטול!</strong> היא מוחקת רשומות שעות ונוכחות בלבד, לא את פרטי העובדים עצמם.</p>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* ── Presence ── */}
          <section id="presence" className="docs-section">
            <button className="section-header" onClick={() => toggle("presence")}>
              <div className="section-title" style={{ color: "#0891b2" }}>
                <Calendar size={22} />
                <span>נוכחות</span>
              </div>
              {isOpen("presence") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("presence") && (
              <div className="section-body">
                <p>עמוד הנוכחות מציג סיכום שעות עבודה לכל עובד לפי חודש שנבחר.</p>
                <div className="step-list">
                  <div className="step">
                    <div className="step-num">1</div>
                    <div>
                      <strong>בחירת חודש</strong>
                      <p>בלחיצה על <em>נוכחות</em> בתפריט, מופיע חלון בחירת חודש. בחר את החודש הרצוי.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">2</div>
                    <div>
                      <strong>מה מוצג</strong>
                      <p>שם עובד, לקוח, שעות רגילות, שעות סוף שבוע, סה"כ שעות, סוג עובד.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">3</div>
                    <div>
                      <strong>חיפוש וסינון</strong>
                      <p>ניתן לחפש לפי שם עובד, לסנן לפי לקוח וסוג עובד.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">4</div>
                    <div>
                      <strong>ייצוא</strong>
                      <p>ניתן לייצא את נתוני הנוכחות לאקסל דרך כפתור הייצוא.</p>
                    </div>
                  </div>
                </div>
                <div className="tip-box">
                  <Info size={16} />
                  <span>נתוני הנוכחות מתמלאים אוטומטית בעת העלאת קבצי T&M וחברות.</span>
                </div>
              </div>
            )}
          </section>

          {/* ── Enforcement ── */}
          <section id="enforcement" className="docs-section">
            <button className="section-header" onClick={() => toggle("enforcement")}>
              <div className="section-title" style={{ color: "#dc2626" }}>
                <Shield size={22} />
                <span>תגבור</span>
              </div>
              {isOpen("enforcement") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("enforcement") && (
              <div className="section-body">
                <p>עמוד תגבור מציג עובדים שנשלחו לתגבור (reinforcement) אצל לקוחות שונים.</p>
                <ul className="docs-list">
                  <li>רשימת תגבורים לפי חודש</li>
                  <li>ניתן להוסיף, לערוך ולמחוק רשומות תגבור</li>
                  <li>מוצג שם עובד, לקוח, תאריכים ושעות</li>
                </ul>
              </div>
            )}
          </section>

          {/* ── Salaries ── */}
          <section id="salaries" className="docs-section">
            <button className="section-header" onClick={() => toggle("salaries")}>
              <div className="section-title" style={{ color: "#16a34a" }}>
                <DollarSign size={22} />
                <span>משכורות</span>
              </div>
              {isOpen("salaries") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("salaries") && (
              <div className="section-body">
                <p>קיימים שלושה עמודי משכורות, כל אחד לסוג עובד שונה:</p>

                <div className="info-cards">
                  <div className="info-card blue">
                    <strong>משכורות קבלנים (/salaries)</strong>
                    <p>מציג את המשכורות המחושבות לעובדי קבלן לפי שעות T&M. מחושב אוטומטית בעת ההעלאה.</p>
                  </div>
                  <div className="info-card green">
                    <strong>משכורות חברה (/salaries/company)</strong>
                    <p>מציג משכורות לעובדי חברה שמקורן בקובץ החברות.</p>
                  </div>
                  <div className="info-card purple">
                    <strong>קבלן וחברה (/salaries/both)</strong>
                    <p>מציג עובדים מסוג "קבלן ו חברה" עם חישוב מעורב.</p>
                  </div>
                </div>

                <h4>פעולות בעמוד המשכורות</h4>
                <div className="step-list">
                  <div className="step">
                    <div className="step-num">1</div>
                    <div>
                      <strong>בחירת חודש</strong>
                      <p>בכניסה לעמוד תתבקש לבחור חודש. ניתן לשנות חודש גם בתוך העמוד.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">2</div>
                    <div>
                      <strong>עריכת משכורת</strong>
                      <p>לחיצה על שורת עובד פותחת חלון עריכה: הוספת תוספות, הפחתות, דלק, הערות. לחץ שמור לאישור.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">3</div>
                    <div>
                      <strong>סימון כשולם</strong>
                      <p>ניתן לסמן משכורת כ"שולמה" — תסומן בצבע ירוק ולא תיכלל בחישובים פתוחים.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">4</div>
                    <div>
                      <strong>ייצוא לאקסל</strong>
                      <p>כפתור ייצוא מוריד את טבלת המשכורות כקובץ Excel.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">5</div>
                    <div>
                      <strong>משכורת גלובלית</strong>
                      <p>עובד עם תג <span className="badge-inline global">גלובלי</span> — המשכורת שלו קבועה ולא מחושבת לפי שעות.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* ── Users ── */}
          <section id="users" className="docs-section">
            <button className="section-header" onClick={() => toggle("users")}>
              <div className="section-title" style={{ color: "#7c3aed" }}>
                <UserPlus size={22} />
                <span>ניהול משתמשים</span>
              </div>
              {isOpen("users") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("users") && (
              <div className="section-body">
                <p>עמוד זה מאפשר למנהל/בעלים ליצור ולנהל משתמשים למערכת.</p>
                <div className="step-list">
                  <div className="step">
                    <div className="step-num">➕</div>
                    <div>
                      <strong>הוספת משתמש</strong>
                      <p>הזן שם משתמש, סיסמה, ובחר הרשאה מהרשימה הנפתחת.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">✏️</div>
                    <div>
                      <strong>עריכת משתמש</strong>
                      <p>ניתן לשנות סיסמה והרשאה של משתמש קיים.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">🗑️</div>
                    <div>
                      <strong>מחיקת משתמש</strong>
                      <p>מחיקת משתמש מהמערכת (לא ניתן למחוק את עצמך).</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* ── Approval ── */}
          <section id="approval" className="docs-section">
            <button className="section-header" onClick={() => toggle("approval")}>
              <div className="section-title" style={{ color: "#059669" }}>
                <UserCheck size={22} />
                <span>אישור עובדים חדשים</span>
              </div>
              {isOpen("approval") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("approval") && (
              <div className="section-body">
                <p>כאשר קובץ T&M מכיל עובד שלא מוכר למערכת, הוא נכנס לתור אישור.</p>
                <div className="step-list">
                  <div className="step">
                    <div className="step-num">1</div>
                    <div>
                      <strong>רשימת ממתינים</strong>
                      <p>מציג עובדים שנמצאו בקובץ T&M ועדיין לא אושרו.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">2</div>
                    <div>
                      <strong>אישור עובד</strong>
                      <p>לחץ <em>אשר</em>, ולאחר מכן מלא את פרטי העובד: סוג, תעריף שעתי, שיטת תשלום, פרטי בנק.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">3</div>
                    <div>
                      <strong>דחייה</strong>
                      <p>לחץ <em>דחה</em> אם העובד אינו צריך להיכנס למערכת.</p>
                    </div>
                  </div>
                </div>
                <div className="tip-box">
                  <Info size={16} />
                  <span>גם מגייסים (recruiter) יכולים להוסיף עובדים חדשים דרך עמוד <em>הוספת עובד</em> — גם הם מופיעים כאן לאישור.</span>
                </div>
              </div>
            )}
          </section>

          {/* ── Profit ── */}
          <section id="profit" className="docs-section">
            <button className="section-header" onClick={() => toggle("profit")}>
              <div className="section-title" style={{ color: "#d97706" }}>
                <Calculator size={22} />
                <span>חישוב רווח</span>
              </div>
              {isOpen("profit") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("profit") && (
              <div className="section-body">
                <p>עמוד זה (לבעלים בלבד) מציג חישוב רווח לפי לקוח וחודש.</p>
                <ul className="docs-list">
                  <li>הכנסה (revenue) לכל לקוח</li>
                  <li>עלות שכר (salaries)</li>
                  <li>עלות אכיפה (enforcement)</li>
                  <li>רווח נטו = הכנסה - שכר - אכיפה</li>
                </ul>
                <p>ניתן להזין הכנסות ידנית ולראות את הרווח בזמן אמת.</p>
              </div>
            )}
          </section>

          {/* ── Report ── */}
          <section id="report" className="docs-section">
            <button className="section-header" onClick={() => toggle("report")}>
              <div className="section-title" style={{ color: "#2563eb" }}>
                <FileSpreadsheet size={22} />
                <span>דוח אקסל</span>
              </div>
              {isOpen("report") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("report") && (
              <div className="section-body">
                <p>עמוד יצירת דוחות Excel לפי חודש ושותפות:</p>
                <div className="info-cards">
                  <div className="info-card blue">
                    <strong>דוח קורל</strong>
                    <p>מייצר Excel עם נוכחות ומשכורות לשותפות קורל.</p>
                  </div>
                  <div className="info-card green">
                    <strong>דוח גולד קלין</strong>
                    <p>מייצר Excel עם נוכחות ומשכורות לשותפות גולד קלין.</p>
                  </div>
                </div>
                <div className="step-list">
                  <div className="step">
                    <div className="step-num">1</div>
                    <div>
                      <strong>בחר חודש</strong>
                      <p>הזן או בחר את החודש הרצוי.</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-num">2</div>
                    <div>
                      <strong>לחץ "צור דוח"</strong>
                      <p>הקובץ יורד אוטומטית לתיקיית ההורדות.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* ── Flows ── */}
          <section id="flows" className="docs-section">
            <button className="section-header" onClick={() => toggle("flows")}>
              <div className="section-title" style={{ color: "#059669" }}>
                <CheckCircle size={22} />
                <span>תהליכים מרכזיים — מדריך שלב אחר שלב</span>
              </div>
              {isOpen("flows") ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {isOpen("flows") && (
              <div className="section-body">

                <div className="flow-block">
                  <h3>🔄 תהליך 1: העלאת קובץ T&M חדש</h3>
                  <div className="flow-steps">
                    <div className="flow-step">
                      <span className="flow-num">1</span>
                      <span>לך ל- <strong>העלאת קבצים</strong></span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">2</span>
                      <span>בחר קובץ Excel מסוג T&M</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">3</span>
                      <span>בחר סוג נתונים: <strong>T&M</strong></span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">4</span>
                      <span>לחץ <strong>"העלה קובץ"</strong> וחכה לסיום</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">5</span>
                      <span>קרא את חלון הסיכום — בדוק שורות שיובאו ועובדים חדשים</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">6</span>
                      <span>לך ל- <strong>אישור עובדים חדשים</strong> ואשר / דחה עובדים ממתינים</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">7</span>
                      <span>בדוק <strong>נוכחות</strong> עבור החודש הרלוונטי</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">8</span>
                      <span>בדוק <strong>משכורות קבלנים</strong> — המשכורות חושבו אוטומטית</span>
                    </div>
                  </div>
                </div>

                <div className="flow-block">
                  <h3>🏢 תהליך 2: העלאת קובץ חברות</h3>
                  <div className="flow-steps">
                    <div className="flow-step">
                      <span className="flow-num">1</span>
                      <span>לך ל- <strong>העלאת קבצים</strong></span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">2</span>
                      <span>בחר קובץ Excel שכל גיליון בו הוא לקוח</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">3</span>
                      <span>בחר סוג נתונים: <strong>חברות</strong></span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">4</span>
                      <span>לחץ <strong>"העלה קובץ"</strong></span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">5</span>
                      <span>בדוק <strong>נוכחות</strong> ו-<strong>משכורות חברה</strong></span>
                    </div>
                  </div>
                </div>

                <div className="flow-block">
                  <h3>👤 תהליך 3: הוספת עובד ידנית</h3>
                  <div className="flow-steps">
                    <div className="flow-step">
                      <span className="flow-num">1</span>
                      <span>לך ל- <strong>עובדים</strong></span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">2</span>
                      <span>לחץ <strong>"הוסף עובד"</strong></span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">3</span>
                      <span>מלא: שם, מספר עובד, סוג (קבלן/חברה/קבלן ו חברה)</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">4</span>
                      <span>מלא: תעריף שעתי, תעריף סופ"ש, שיטת תשלום</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">5</span>
                      <span>מלא פרטי בנק (בנק, חשבון, סניף) אם רלוונטי</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">6</span>
                      <span>בחר לקוח משויך מהרשימה הנפתחת</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">7</span>
                      <span>לחץ <strong>"שמור"</strong></span>
                    </div>
                  </div>
                </div>

                <div className="flow-block">
                  <h3>✏️ תהליך 4: עריכת פרטי עובד</h3>
                  <div className="flow-steps">
                    <div className="flow-step">
                      <span className="flow-num">1</span>
                      <span>לך ל- <strong>עובדים</strong> וחפש את העובד</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">2</span>
                      <span>לחץ על כפתור העריכה (עיפרון) בשורת העובד</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">3</span>
                      <span>שנה את הפרטים הנדרשים</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">4</span>
                      <span>לחץ <strong>"שמור"</strong> לאישור השינויים</span>
                    </div>
                  </div>
                </div>

                <div className="flow-block">
                  <h3>🗑️ תהליך 5: מחיקת עובד</h3>
                  <div className="flow-steps">
                    <div className="flow-step">
                      <span className="flow-num">1</span>
                      <span>לך ל- <strong>עובדים</strong> וחפש את העובד</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">2</span>
                      <span>לחץ על כפתור המחיקה (פח) בשורת העובד</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step danger">
                      <span className="flow-num">3</span>
                      <span>אשר את הפעולה בחלון האישור — <strong>פעולה זו לא ניתנת לביטול!</strong></span>
                    </div>
                  </div>
                </div>

                <div className="flow-block">
                  <h3>💰 תהליך 6: עדכון ותשלום משכורת</h3>
                  <div className="flow-steps">
                    <div className="flow-step">
                      <span className="flow-num">1</span>
                      <span>לך ל- <strong>משכורות</strong> ובחר חודש</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">2</span>
                      <span>לחץ על שורת העובד לעריכה</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">3</span>
                      <span>הוסף תוספות, הפחתות, דלק, הערות לפי הצורך</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">4</span>
                      <span>לחץ <strong>"שמור"</strong></span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">5</span>
                      <span>לאחר ביצוע התשלום — סמן <strong>"שולמה"</strong> על המשכורת</span>
                    </div>
                  </div>
                </div>

                <div className="flow-block">
                  <h3>🔄 תהליך 7: מחיקה ו-העלאה מחדש של חודש</h3>
                  <div className="flow-steps">
                    <div className="flow-step">
                      <span className="flow-num">1</span>
                      <span>לך ל- <strong>העלאת קבצים</strong></span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">2</span>
                      <span>גלול לאזור <em>"מחיקת נתונים שהועלו"</em></span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step danger">
                      <span className="flow-num">3</span>
                      <span>בחר את החודש וסוג הנתונים → לחץ <strong>"מחק נתונים"</strong> ואשר</span>
                    </div>
                    <div className="flow-arrow">↓</div>
                    <div className="flow-step">
                      <span className="flow-num">4</span>
                      <span>העלה מחדש את הקובץ המתוקן</span>
                    </div>
                  </div>
                </div>

              </div>
            )}
          </section>

        </main>
      </div>
    </div>
  );
};

export default Docs;
